import { useCallback, useEffect, useState } from 'react';
import { holidayApi } from '../api/HolidayApi';
import { HolidayDto, HolidayForm, SaveHolidayRequest } from '../types/holiday.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useUserInfo } from '@/shared/hooks/useUserInfo';

const CURRENT_YEAR = String(new Date().getFullYear());

export const useHolidayManagement = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [holidays, setHolidays] = useState<HolidayDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [yearFilter, setYearFilter] = useState(CURRENT_YEAR);
    const [syncing, setSyncing] = useState(false);

    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        try {
            const res = await holidayApi.getHolidays(yearFilter || undefined);
            setHolidays(res.holidays ?? []);
        } catch {
            showToast('공휴일 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast, yearFilter]);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    const saveHoliday = useCallback(async (form: HolidayForm): Promise<boolean> => {
        const userInfo = getUserInfo();
        if (!userInfo) return false;

        const req: SaveHolidayRequest = {
            seqNo: form.seqNo,
            holidayDate: form.holidayDate.trim(),
            holidayName: form.holidayName.trim(),
            targetDow: Number(form.targetDow),
            userId: userInfo.userId,
        };

        try {
            await holidayApi.saveHoliday(req);
            showToast(form.seqNo ? '수정되었습니다.' : '등록되었습니다.', { type: ToastType.SUCCESS });
            await fetchHolidays();
            return true;
        } catch (error: any) {
            const msg = error?.message || '저장에 실패했습니다.';
            showToast(msg, { type: ToastType.ERROR });
            return false;
        }
    }, [getUserInfo, showToast, fetchHolidays]);

    const deleteHoliday = useCallback(async (seqNo: number): Promise<boolean> => {
        try {
            await holidayApi.deleteHoliday(seqNo);
            showToast('삭제되었습니다.', { type: ToastType.SUCCESS });
            await fetchHolidays();
            return true;
        } catch {
            showToast('삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        }
    }, [showToast, fetchHolidays]);

    const syncHolidays = useCallback(async (): Promise<void> => {
        const year = Number(yearFilter || CURRENT_YEAR);
        if (!year || isNaN(year)) {
            alert('연도를 입력해주세요.');
            return;
        }
        const userInfo = getUserInfo();
        if (!userInfo) return;

        setSyncing(true);
        try {
            const res = await holidayApi.syncKoreanHolidays(year, userInfo.userId);
            showToast(`${year}년 공휴일 동기화 완료 (${res.savedCount}건 추가/복구)`, { type: ToastType.SUCCESS });
            await fetchHolidays();
        } catch (error: any) {
            showToast(error?.message || '동기화에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSyncing(false);
        }
    }, [yearFilter, getUserInfo, showToast, fetchHolidays]);

    return {
        holidays,
        loading,
        yearFilter,
        setYearFilter,
        syncing,
        saveHoliday,
        deleteHoliday,
        syncHolidays,
    };
};
